import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { CheckLg, ChevronDown } from 'react-bootstrap-icons';

import { MapProvider } from '../../context/map';
import useTranslation from '../../hooks/use_translation';
import useWindowSize from '../../hooks/use_window_resize';
import LocationMap from './map';
import LocationMapGeoLocation from './map_geo_location';
import LocationMapList from './map_list';
import LocationMarkers from './map_markers';
import LocationMapSearch from './map_search';

const typesMap = {
	blood_sample: [{ label: 't2' }],
	mri: [{ label: 't3' }],
};

const MapSection = ({ include_locations, title, types }) => {
	const { t } = useTranslation('component_map_section');
	const [, setHide] = useState(false);
	const { width } = useWindowSize();
	const [showList, setShowList] = useState(false); // default: dold

	useEffect(() => {
		if (width !== null) {
			setShowList(width >= 768);
		}
	}, [width]);

	const handleGotoMarker = () => setHide(true);

	const availableTypes = types?.length > 0 ? types : Object.keys(typesMap);

	const [selectedTypes, setSelectedTypes] = useState(
		availableTypes.reduce((acc, type) => {
			acc[type] = true;
			return acc;
		}, {}),
	);

	const handleToggleType = (type) => {
		setSelectedTypes((prev) => ({
			...prev,
			[type]: !prev[type],
		}));
	};

	const activeTypes = Object.keys(selectedTypes).filter(
		(type) => selectedTypes[type],
	);

	return (
		<section className="location">
			<MapProvider>
				<div className="location-map-container position-relative">
					<LocationMap />
					<LocationMarkers
						include_diagnostic_types={activeTypes}
						include_locations={include_locations}
					/>

					<div className="location-list-left p-3">
						<div className="p-3 bg-white shadow-sm rounded-3 border border-1">
							{title && <h3 className="large fw-700 mb-3">{title}</h3>}
							<LocationMapSearch />

							<div className="d-flex justify-content-between align-items-center">
								{availableTypes.length > 1 && (
									<div className="btn-group">
										<button
											aria-expanded="false"
											className={`btn btn-light dropdown-toggle d-flex align-items-center gap-2 xs rounded-pill ${activeTypes.length > 0 ? 'bg-lime' : ''}`}
											data-bs-toggle="dropdown"
											type="button"
										>
											{activeTypes.length > 0 && (
												<CheckLg className="text-success" />
											)}

											{t('t1')}
											{activeTypes.length > 0 && (
												<>
													{' '}
													{'('}
													{activeTypes.length}
													{')'}
												</>
											)}
										</button>
										<ul className="dropdown-menu p-3">
											<li>
												<h6 className="dropdown-header ps-0">{t('t1')}</h6>
											</li>
											{availableTypes.map((type, index) => {
												const label = typesMap[type]?.[0]?.label || type;
												return (
													<li className="form-check my-2" key={index}>
														<input
															checked={selectedTypes[type]}
															className="form-check-input"
															id={`type-check-${index}`}
															onChange={() => handleToggleType(type)}
															type="checkbox"
														/>
														<label
															className="form-check-label"
															htmlFor={`type-check-${index}`}
														>
															{t(label)}
														</label>
													</li>
												);
											})}
										</ul>
									</div>
								)}

								<LocationMapGeoLocation />
							</div>
						</div>
						{!showList && (
							<div className="mt-2">
								<button
									className="btn btn-light text-primary bg-white shadow-sm w-100 rounded-pill"
									onClick={() => setShowList(true)}
								>
									{t('t4')} <ChevronDown />
								</button>
							</div>
						)}
						{showList && (
							<div className="o-hidden rounded-3 mt-2 border border-1 bg-white shadow-sm position-relative location-list-wrapper">
								<div className="d-flex flex-column">
									<div className="d-flex justify-content-end p-3">
										<button
											aria-label="Close"
											className="btn-close"
											onClick={() => setShowList(false)}
											type="button"
										/>
									</div>
									<div className="location-list px-3">
										<LocationMapList handleGotoMarker={handleGotoMarker} />
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</MapProvider>
		</section>
	);
};

MapSection.propTypes = {
	include_locations: PropTypes.arrayOf(PropTypes.number, PropTypes.string),
	title: PropTypes.string,
	types: PropTypes.array,
};

export default MapSection;
